'use client';

import React, { useEffect, useState } from 'react';

import { Modal } from '@bytel/trilogy-react';

import { getPartnerProductTradeInQuestions, getPartnerProductTradeInQuote } from '@services/trade-in';

import { ContractType } from '@app-types/contract';
import { TradeInCriteriaResponsesType, TradeInCriteriaType, TradeInQuoteType } from '@app-types/trade-in';

import { TradeInStep } from '@constants/trade-in';

import {
    ReturnSessionTradeInProduct,
    SetSessionTradeInConfirmationInput,
    getSessionTradeInStep,
    setSessionTradeInStep,
} from '@product-detail/helpers';

import { MobileTradeInStep1 } from './mobile-trade-in-step1';
import { MobileTradeInStep2 } from './mobile-trade-in-step2';
import { MobileTradeInStep3 } from './mobile-trade-in-step3';

export type MobileTradeInModalProps = {
    isOpen: boolean;
    onClose: () => void;
    step: number;
    setStep: (step: number) => void;
    productName: string;
    tradeInBonus: number;
    tradeInBonusPlan?: string;
    contract?: ContractType;
    isRenewal: boolean;
};

export function MobileTradeInModal({
    isOpen = false,
    onClose,
    step,
    setStep,
    productName,
    tradeInBonus,
    tradeInBonusPlan,
    contract,
    isRenewal,
}: MobileTradeInModalProps) {
    const [quote, setQuote] = useState<TradeInQuoteType | undefined>(undefined);
    const [questions, setQuestions] = useState<TradeInCriteriaType>([]);
    const [isLoadingQuote, setIsLoadingQuote] = useState(true);
    const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);

    const handleGetQuestions = (productId: string) => {
        setIsLoadingQuestions(true);
        getPartnerProductTradeInQuestions(productId).then((data) => {
            setIsLoadingQuestions(false);
            setQuestions(data);
        });
    };

    const handleGetQuote = (
        productId: string,
        criterias: TradeInCriteriaResponsesType['items'],
        abortController?: AbortController,
    ) => {
        setIsLoadingQuote(true);
        getPartnerProductTradeInQuote(productId, criterias, abortController).then((data) => {
            setIsLoadingQuote(false);
            setQuote(data);
            const tradeInStepConfirmation: SetSessionTradeInConfirmationInput = {
                estimation: { amount: data.estimatedRecoveryAmount },
                recoveryId: data.id,
                questions: criterias,
            };
            setSessionTradeInStep(TradeInStep.CONFIRMATION, tradeInStepConfirmation);
        });
    };

    useEffect(() => {
        const product = getSessionTradeInStep(TradeInStep.SEARCH) as ReturnSessionTradeInProduct[TradeInStep.SEARCH];

        if (step === TradeInStep.FORM && questions.length <= 0) {
            handleGetQuestions(product!.data.id);
        }

        if (step === TradeInStep.CONFIRMATION && !quote) {
            const valueStep = getSessionTradeInStep(TradeInStep.FORM) as ReturnSessionTradeInProduct[TradeInStep.FORM];
            handleGetQuote(product!.data.id, valueStep!);
        }
    }, [step, questions, quote]);

    return (
        <Modal active={isOpen} onClose={onClose} title='Reprise mobile'>
            <MobileTradeInStep1
                step={step}
                setStep={setStep}
                productName={productName}
                tradeInBonusPlan={tradeInBonusPlan}
                tradeInBonus={tradeInBonus}
                getQuestions={handleGetQuestions}
            />
            <MobileTradeInStep2
                step={step}
                setStep={setStep}
                closeModal={onClose}
                questions={questions}
                getQuote={handleGetQuote}
                isLoading={isLoadingQuestions}
            />
            <MobileTradeInStep3
                step={step}
                setStep={setStep}
                quote={quote}
                tradeInBonus={tradeInBonus}
                closeModal={onClose}
                isLoading={isLoadingQuote}
                contract={contract}
                isRenewal={isRenewal}
            />
        </Modal>
    );
}
